import React, { useState, useEffect, useRef } from "react";
import {
  Box, Typography, Grid, TextField, FormControl, FormLabel, FormGroup, FormControlLabel, Checkbox, Select, MenuItem,
  RadioGroup, Radio, Button, Paper, Divider, Card, CardContent, IconButton, Avatar, Chip, Stack,
  InputAdornment, Tooltip, useMediaQuery, useTheme,
  TextareaAutosize,
  CircularProgress,
} from "@mui/material";
import {
  Business, Person, LocationOn, Phone, Email, WorkOutline, People, AttachMoney, CalendarToday, Description, ThumbUp,
  PhotoLibrary, Warehouse, Gavel, VerifiedUser, Subscriptions, Payment, LocalShipping, Save, AddPhotoAlternate, Videocam, YouTube,
  ArrowBack,
  Close,
  Description as DescriptionIcon,
} from "@mui/icons-material";
import styles from "./styles";
import MiniDrawer from "../../Components/Drawer";
import { getApiData, putApiData, fetchHelper, getAxiosApi } from "../../Apis/apiHelper";
import BaseSetting from "../../Apis/setting";
import { toast } from "react-toastify";
import CSelect from "../../Components/CSelect";
import _ from "lodash";
import BaseColor from "../../Config/Color";
import { Editor } from "@tinymce/tinymce-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import TagsInput from "../../Components/TagsInput";

// Utility function to clean null, undefined, or 'null' string values recursively
function cleanNulls(obj) {
  if (Array.isArray(obj)) {
    return obj.map(cleanNulls);
  } else if (obj && typeof obj === "object") {
    const cleaned = {};
    for (const key in obj) {
      const value = obj[key];
      if (value === null || value === undefined || value === "null") {
        cleaned[key] = "";
      } else if (typeof value === "object") {
        cleaned[key] = cleanNulls(value);
      } else {
        cleaned[key] = value;
      }
    }
    return cleaned;
  }
  return obj;
}

const UserCompanyForm = () => {
  const classes = styles();
  const navigate = useNavigate();

  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const years = Array.from(
    { length: 50 },
    (_, i) => new Date().getFullYear() - i
  );

  // State for address fields
  const [officeStateList, setOfficeStateList] = useState([]);
  const [officeCityList, setOfficeCityList] = useState([]);

  // Form state
  const [subscriptionLevel, setSubscriptionLevel] = useState("free");
  const [userDetail, setUserDetail] = useState({});
  const [photos, setPhotos] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [companyBrochure, setCompanyBrochure] = useState(null);
  const [companyBrochureOriginalName, setCompanyBrochureOriginalName] = useState("");
  const [removeBrochure, setRemoveBrochure] = useState(false);
  const [companyVideo, setCompanyVideo] = useState(null);
  const [companyVideoPreview, setCompanyVideoPreview] = useState("");
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyLogoPreview, setCompanyLogoPreview] = useState(null);

  // Address related states
  const [countryList, setCountryList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [cityList, setCityList] = useState([]);

  const [userCountry, setUserCountry] = useState(null);
  const [userState, setUserState] = useState(null);
  const [userCity, setUserCity] = useState(null);

  const [officeCountry, setOfficeCountry] = useState(null);
  const [officeState, setOfficeState] = useState(null);
  const [officeCity, setOfficeCity] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [phoneCodeList, setPhoneCodeList] = useState([]);
  const [selectedPhoneCode, setSelectedPhoneCode] = useState(null);

  // Add state for meta keywords input and tag list
  const [metaKeywordInput, setMetaKeywordInput] = useState("");
  const [metaKeywords, setMetaKeywords] = useState([]);
  // Additional meta keywords states for specific pages
  const [metaKeywordInputAboutUs, setMetaKeywordInputAboutUs] = useState("");
  const [metaKeywordsAboutUs, setMetaKeywordsAboutUs] = useState([]);
  const [metaKeywordInputContactUs, setMetaKeywordInputContactUs] = useState("");
  const [metaKeywordsContactUs, setMetaKeywordsContactUs] = useState([]);
  const [metaKeywordInputOurProducts, setMetaKeywordInputOurProducts] = useState("");
  const [metaKeywordsOurProducts, setMetaKeywordsOurProducts] = useState([]);
  const [applyGlobalMeta, setApplyGlobalMeta] = useState(false);

  const validatePhoneNumber = (phone, isSubmission = false) => {
    if (!phone || phone.trim() === '') {
      return isSubmission ? "Mobile number is required" : "";
    }

    const phoneRegex = /^[\+]?[0-9\s\-\(\)]{10}$/;
    if (!phoneRegex.test(phone.trim())) {
      return "Please enter a valid mobile number";
    }

    return "";
  };

  const GetCountryListApi = () => {
    const endpoint = BaseSetting.endpoint.allCountryList;
    const params = { type: "web", web: true };

    getApiData(endpoint, "post", params)
      .then((result) => {
        if (result.status) {
          const data = result?.data?.countryData;
          if (Array.isArray(data) && data.length > 0) {
            setCountryList(data);
          } else {
            toast("No countries found.", { type: "warning" });
          }
        } else {
          toast(result.message || "Failed to fetch country list.", { type: "error" });
        }
      })
      .catch((err) => {
        toast(err.message || "Error fetching country list.", { type: "error" });
      });
  };

  const GetPhoneCodeListApi = () => {
    const endpoint = `${BaseSetting.endpoint.countriesList}`;
    getApiData(endpoint, "post", { download: true, type: "web" })
      .then((result) => {
        if (result?.status) {
          const response = result?.data;
          setPhoneCodeList(response?.countryData || []);
        } else {
          toast(result?.message || "Failed to fetch phone codes.", { type: "error" });
        }
      })
      .catch((err) => {
        toast(err?.message || "Error fetching phone codes.", { type: "error" });
      });
  };

  useEffect(() => {
    GetCountryListApi();
    GetPhoneCodeListApi();
  }, []);

  const editorRef = useRef(null);

  const getUserDetail = () => {
    const id = searchParams.get("id");

    if (id) {
      const endpoint = `${BaseSetting.endpoint.getUserById}/${id}`;
      getApiData(endpoint, "GET", {})
        .then((res) => {
          if (res?.status) {
            // Clean the data before setting state
            const cleanedData = cleanNulls(res?.data);
            setUserDetail(cleanedData);
            const photoUrls = cleanedData?.businessInfo?.company_photoes || [];
            setPhotos(photoUrls);
            setPhotoPreviews(photoUrls);
            setCompanyLogo(cleanedData?.businessInfo?.company_logo || null);
            setCompanyLogoPreview(cleanedData?.businessInfo?.company_logo || null);
            // Set metaKeywords from businessInfo.meta_keywords (array or comma-separated string)
            let metaKeys = cleanedData?.businessInfo?.meta_keywords;
            if (typeof metaKeys === 'string') {
              metaKeys = metaKeys.split(',').map(kw => kw.trim()).filter(kw => kw);
            }
            setMetaKeywords(Array.isArray(metaKeys) ? metaKeys : []);
            setMetaKeywordInput("");
            // Set About Us meta keywords
            let metaKeysAbout = cleanedData?.businessInfo?.meta_keywords_about_us;
            if (typeof metaKeysAbout === 'string') {
              metaKeysAbout = metaKeysAbout.split(',').map(kw => kw.trim()).filter(kw => kw);
            }
            setMetaKeywordsAboutUs(Array.isArray(metaKeysAbout) ? metaKeysAbout : []);
            setMetaKeywordInputAboutUs("");
            // Set Contact Us meta keywords
            let metaKeysContact = cleanedData?.businessInfo?.meta_keywords_contact_us;
            if (typeof metaKeysContact === 'string') {
              metaKeysContact = metaKeysContact.split(',').map(kw => kw.trim()).filter(kw => kw);
            }
            setMetaKeywordsContactUs(Array.isArray(metaKeysContact) ? metaKeysContact : []);
            setMetaKeywordInputContactUs("");
            // Set Our Products meta keywords
            let metaKeysProducts = cleanedData?.businessInfo?.meta_keywords_our_products;
            if (typeof metaKeysProducts === 'string') {
              metaKeysProducts = metaKeysProducts.split(',').map(kw => kw.trim()).filter(kw => kw);
            }
            setMetaKeywordsOurProducts(Array.isArray(metaKeysProducts) ? metaKeysProducts : []);
            setMetaKeywordInputOurProducts("");
            const countryData = cleanedData?.businessInfo?.country;
            setUserCountry({ name: countryData });
            const stateData = cleanedData?.businessInfo?.state;
            setUserState({ name: stateData });
            const cityData = cleanedData?.businessInfo?.city;
            setUserCity({ name: cityData });
            const officeCountryData = cleanedData?.businessInfo?.office_country;
            setOfficeCountry({ name: officeCountryData });
            const officeStateData = cleanedData?.businessInfo?.office_state;
            setOfficeState({ name: officeStateData });
            const officeCityData = cleanedData?.businessInfo?.office_city;
            setOfficeCity({ name: officeCityData });
            setCompanyVideo(cleanedData?.businessInfo?.company_videos || null);
            setCompanyVideoPreview(cleanedData?.businessInfo?.company_videos || null);

            // Set company brochure data
            setCompanyBrochureOriginalName(cleanedData?.businessInfo?.company_brochure_original_name || "");
            setRemoveBrochure(false);

            if (cleanedData?.businessInfo?.company_brochure) {
              setCompanyBrochure(cleanedData?.businessInfo?.company_brochure);
            }
          }
        });
    }
  };


  useEffect(() => {
    const id = searchParams.get("id");
    if (id && phoneCodeList.length > 0) {
      getUserDetail();
    }
  }, [phoneCodeList.length > 0]);

  useEffect(() => {
    if (userDetail?.phone_code_id && phoneCodeList.length > 0) {
      const phoneCode = phoneCodeList.find(code => code.phone_code === userDetail.phone_code_id);
      if (phoneCode) {
        setSelectedPhoneCode(phoneCode);
      }
    } else if (phoneCodeList.length > 0 && !selectedPhoneCode) {
      const defaultPhoneCode = phoneCodeList.find(code => code.phone_code === "+91");
      if (defaultPhoneCode) {
        setSelectedPhoneCode(defaultPhoneCode);
      }
    }
  }, [phoneCodeList, userDetail?.phone_code_id]);

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);

    // Only allow up to 5 photos in total
    if (photos.length + files.length > 5) {
      alert("You can only upload up to 5 photos.");
      return;
    }

    // Only accept image files under 1MB
    const validFiles = files.filter(file => file.size <= 1024 * 1024 && file.type.startsWith('image/'));
    if (validFiles.length !== files.length) {
      alert("Each photo must be an image and less than 1MB.");
    }

    const newPreviews = validFiles.map(file => URL.createObjectURL(file));

    setPhotos(prev => [...prev, ...validFiles]);
    setPhotoPreviews(prev => [...prev, ...newPreviews]);
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
    setPhotoPreviews(prev => {
      // Revoke the object URL if it was created
      const previewToRemove = prev[index];
      if (previewToRemove?.startsWith('blob:')) {
        URL.revokeObjectURL(previewToRemove);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleCompanyBrochureUpload = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    // Check file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      alert("Please upload only PDF or DOC/DOCX files.");
      return;
    }

    // Check file size (5MB = 5 * 1024 * 1024 bytes)
    if (file.size > 5 * 1024 * 1024) {
      alert("File size must be under 5MB.");
      return;
    }

    setCompanyBrochure(file);
    setCompanyBrochureOriginalName(file.name);
    setRemoveBrochure(false); // Reset remove flag when new file is uploaded
  };

  const removeCompanyBrochure = () => {
    setCompanyBrochure(null);
    setCompanyBrochureOriginalName("");
    setRemoveBrochure(true);

    // Also clear the existing brochure data from userDetail
    if (userDetail?.businessInfo?.company_brochure) {
      setUserDetail({
        ...userDetail,
        businessInfo: {
          ...userDetail.businessInfo,
          company_brochure: null,
          company_brochure_original_name: null,
        },
      });
    }
  };

  const hasCompanyBrochure = () => {
    return companyBrochure || userDetail?.businessInfo?.company_brochure;
  };

  const getCompanyBrochureDisplayName = () => {
    if (companyBrochure instanceof File) {
      return companyBrochure.name;
    }
    return companyBrochureOriginalName || userDetail?.businessInfo?.company_brochure_original_name || "Company Brochure";
  };

  const handleCompanyVideoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      alert("Please upload a valid video file.");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      alert("Video size must be under 20MB.");
      return;
    }
    setCompanyVideo(file);
    setCompanyVideoPreview(URL.createObjectURL(file));
  };

  const removeCompanyVideo = () => {
    if (companyVideoPreview) URL.revokeObjectURL(companyVideoPreview);
    setCompanyVideo(null);
    setCompanyVideoPreview("");
  };

  const handleCompanyLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 1024 * 1024 || !file.type.startsWith('image/')) {
      alert("Logo must be an image and less than 1MB.");
      return;
    }
    setCompanyLogo(file);
    setCompanyLogoPreview(URL.createObjectURL(file));
  };

  const removeCompanyLogo = () => {
    // Revoke the object URL if it was created for a File
    if (companyLogoPreview && typeof companyLogoPreview === 'string' && companyLogoPreview.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(companyLogoPreview);
      } catch (e) {
        // Ignore errors
      }
    }
    setCompanyLogo(null);
    setCompanyLogoPreview(null);
  };

  const updateUserDetails = () => {
    // Clear previous errors
    setPhoneError("");

    // Validate phone code
    if (!selectedPhoneCode) {
      toast("Please select a phone code", { type: "error" });
      return;
    }

    // Validate phone number
    const phoneValidationError = validatePhoneNumber(userDetail?.phone, true);
    if (phoneValidationError) {
      setPhoneError(phoneValidationError);
      return;
    }

    setIsLoading(true);
    const payload = new FormData();

    // Helper function to append non-null/undefined values
    const appendIfValid = (key, value) => {
      if (value !== null && value !== undefined && value !== "") {
        payload.append(key, value);
      }
    };

    console.log("usercointry", userCountry)

    // Append basic user details
    appendIfValid("first_name", userDetail?.first_name);
    appendIfValid("last_name", userDetail?.last_name);
    appendIfValid("language_id", 1); // Static value, always included
    appendIfValid("name", userDetail?.businessInfo?.name);
    appendIfValid("type", userDetail?.businessInfo?.type || 0);
    appendIfValid("website", userDetail?.businessInfo?.website);
    appendIfValid("emailOrPhone", userDetail?.email);
    appendIfValid("email", userDetail?.email);
    appendIfValid("phone_code_id", selectedPhoneCode?.phone_code);
    appendIfValid("phone", userDetail?.phone);
    appendIfValid("landline_number", userDetail?.businessInfo?.landline_number);
    appendIfValid("zipcode", userDetail?.businessInfo?.zipcode);
    appendIfValid("per_country", userDetail?.businessInfo?.per_country);
    appendIfValid("per_country_code", userDetail?.businessInfo?.per_country_code);
    appendIfValid("bus_country", userDetail?.businessInfo?.bus_country);
    appendIfValid("bus_country_code", userDetail?.businessInfo?.bus_country_code);
    appendIfValid("company_ceo_name", userDetail?.businessInfo?.company_ceo_name);
    appendIfValid("country", userCountry?.name);
    appendIfValid("country_code", userCountry?.country_code);
    appendIfValid("state", userState?.name);
    appendIfValid("city", userCity?.name);
    appendIfValid("street_name", userDetail?.businessInfo?.street_name);
    appendIfValid("description", userDetail?.businessInfo?.description);
    appendIfValid("total_no_of_employee", userDetail?.businessInfo?.total_no_of_employee);
    appendIfValid("annual_turn_over", userDetail?.businessInfo?.annual_turn_over);
    appendIfValid("company_founding_year", userDetail?.businessInfo?.company_founding_year);
    appendIfValid("company_description", userDetail?.businessInfo?.company_description);
    appendIfValid("why_choose_us", userDetail?.businessInfo?.why_choose_us);
    appendIfValid("youtube_link", userDetail?.businessInfo?.youtube_link);
    appendIfValid("office_country", officeCountry?.name);
    appendIfValid("office_state", officeState?.name);
    appendIfValid("office_city", officeCity?.name);
    appendIfValid("office_zipcode", userDetail?.businessInfo?.office_zipcode);
    appendIfValid("office_address_details", userDetail?.businessInfo?.office_address_details);
    appendIfValid("warehouse_address", userDetail?.businessInfo?.warehouse_address);
    appendIfValid("GST_number", userDetail?.businessInfo?.GST_number);
    appendIfValid("GST_partner_name", userDetail?.businessInfo?.GST_partner_name);
    appendIfValid("GST_registration_date", userDetail?.businessInfo?.GST_registration_date);
    appendIfValid("is_verified", userDetail?.is_verified);
    appendIfValid("legal_status_of_firm", userDetail?.businessInfo?.legal_status_of_firm);
    appendIfValid("import_export_code", userDetail?.businessInfo?.import_export_code);
    appendIfValid("full_user_address", userDetail?.businessInfo?.full_user_address);
    appendIfValid("full_office_address", userDetail?.businessInfo?.full_office_address);
    appendIfValid("warehouse_details", userDetail?.businessInfo?.warehouse_details);
    appendIfValid("our_aim", userDetail?.businessInfo?.our_aim);
    appendIfValid("our_vision", userDetail?.businessInfo?.our_vision);

    // Append company photos
    photos?.forEach((photo) => {
      if (photo instanceof File || typeof photo === "string") {
        payload.append("company_photoes", photo);
      }
    });

    // Append company brochure
    if (companyBrochure instanceof File) {
      payload.append("company_brochure", companyBrochure);
      appendIfValid("company_brochure_original_name", companyBrochureOriginalName);
    } else if (removeBrochure) {
      payload.append("remove_company_brochure", "true");
    }

    // Append payment methods
    if (Array.isArray(userDetail?.businessInfo?.payment_method)) {
      userDetail?.businessInfo?.payment_method?.forEach((method) => {
        appendIfValid("payment_method", method?.trim());
      });
    }

    // Append shipment methods
    if (Array.isArray(userDetail?.businessInfo?.shipment_method)) {
      userDetail?.businessInfo?.shipment_method?.forEach((method) => {
        appendIfValid("shipment_method", method?.trim());
      });
    }

    // Append business types
    if (Array.isArray(userDetail?.businessInfo?.business_type)) {
      userDetail?.businessInfo?.business_type?.forEach((type, index) => {
        appendIfValid(`business_type[${index}]`, type);
      });
    }

    // Append FAQs
    if (Array.isArray(userDetail?.businessInfo?.faqs)) {
      userDetail.businessInfo.faqs
        .filter((faq) => faq.question && faq.answer)
        .forEach((faq, idx) => {
          appendIfValid(`faqs[${idx}][question]`, faq.question);
          appendIfValid(`faqs[${idx}][answer]`, faq.answer);
        });
    }

    // Append company videos
    if (companyVideo) {
      const videos = Array.isArray(companyVideo) ? companyVideo : [companyVideo];
      videos.forEach((video) => {
        if (video instanceof File || typeof video === "string") {
          payload.append("company_videos", video);
        }
      });
    }

    // Append company logos
    if (companyLogo) {
      const logos = Array.isArray(companyLogo) ? companyLogo : [companyLogo];
      logos.forEach((logo) => {
        if (logo instanceof File || typeof logo === "string") {
          payload.append("company_logo", logo);
        }
      });
    }

    // Append meta keywords
    appendIfValid("meta_keywords", metaKeywords.join(", "));
    appendIfValid("meta_description", userDetail?.businessInfo?.meta_description);
    appendIfValid("meta_title", userDetail?.businessInfo?.meta_title);
    // Append additional meta fields for specific pages
    appendIfValid("meta_title_about_us", userDetail?.businessInfo?.meta_title_about_us);
    appendIfValid("meta_description_about_us", userDetail?.businessInfo?.meta_description_about_us);
    appendIfValid("meta_keywords_about_us", metaKeywordsAboutUs.join(", "));
    appendIfValid("meta_title_contact_us", userDetail?.businessInfo?.meta_title_contact_us);
    appendIfValid("meta_description_contact_us", userDetail?.businessInfo?.meta_description_contact_us);
    appendIfValid("meta_keywords_contact_us", metaKeywordsContactUs.join(", "));
    appendIfValid("meta_title_our_products", userDetail?.businessInfo?.meta_title_our_products);
    appendIfValid("meta_description_our_products", userDetail?.businessInfo?.meta_description_our_products);
    appendIfValid("meta_keywords_our_products", metaKeywordsOurProducts.join(", "));

    const endpoint = `${BaseSetting.endpoint.updateUserDetails}/${userDetail?.id}`;
    getAxiosApi(endpoint, "post", payload)
      .then((res) => {
        setIsLoading(false);
        if (res?.status) {
          toast("User details updated successfully", { type: "success" });
          navigate(`/company-users?page=1&status=approved`);
        } else {
          toast(res?.message || "Failed to update user details.", { type: "error" });
        }
      })
      .catch((error) => {
        setIsLoading(false);
        const errorMessage =
          error?.response?.data?.message || error?.message || "Error updating user details.";
        toast(errorMessage, { type: "error" });
      });
  };

  useEffect(() => {
    if (userCountry) {
      fetchStatesAndCities(
        userCountry?.name,
        setStateList,
        setCityList,
        setOfficeState,
        setOfficeCity
      );
    } else {
      setStateList([]);
      setCityList([]);
    }
  }, [userCountry]);

  useEffect(() => {
    if (userState) {
      const cities =
        stateList.find((s) => s.name === userState?.name)?.cities || [];
      setCityList(cities);
    } else {
      setCityList([]);
    }
  }, [userState]);

  const fetchStatesAndCities = async (
    countryName,
    stateSetter,
    citySetter,
    selectedStateSetter,
    selectedCitySetter
  ) => {
    try {
      const response = await getApiData(
        BaseSetting.endpoint.countryStateCityListByPincode,
        "post",
        { country: countryName }
      );
      if (response?.status) {
        const states = response?.data?.states || [];
        stateSetter(states);
        citySetter([]);
      } else {
        stateSetter([]);
        citySetter([]);
      }
    } catch (error) {
      stateSetter([]);
      citySetter([]);
    }
  };

  const renderFormSection = (title, icon, children) => (
    <Card className={classes.formSection} elevation={3}>
      <CardContent>
        <Box display="flex" alignItems="center" mb={2}>
          {icon}
          <Typography variant="h6" className={classes.sectionTitle} ml={1}>
            {title}
          </Typography>
        </Box>
        <Divider sx={{ mb: 3 }} />
        {children}
      </CardContent>
    </Card>
  );
  const findAndSetUserCountry = (countryName) => {
    const matchedCountry = countryList.find(country => country.name === countryName);
    if (matchedCountry) {
      setUserCountry(matchedCountry);
    }
  };

  useEffect(() => {
    // Call this function when the component mounts or when the countryList is updated
    if (userCountry?.name && countryList.length > 0) {
      findAndSetUserCountry(userCountry.name);
    }
  }, [userDetail]);

  const findAndSetUserState = (stateName) => {
    const matchedState = stateList.find(state => state.name === stateName);
    if (matchedState) {
      setUserState(matchedState);
    }
  };

  const findAndSetUserCity = (cityName) => {
    const matchedCity = cityList.find(city => city.name === cityName);
    if (matchedCity) {
      setUserCity(matchedCity);
    }
  };

  useEffect(() => {
    // Call these functions when the component mounts or when the lists are updated
    if (userState?.name && stateList.length > 0) {
      findAndSetUserState(userState.name);
    }
    if (userCity?.name && cityList.length > 0) {
      findAndSetUserCity(userCity.name);
    }
  }, [stateList, cityList, userState?.name, userCity?.name]);

  useEffect(() => {
    if (officeCountry) {
      fetchStatesAndCities(
        officeCountry?.name,
        setOfficeStateList,
        setOfficeCityList,
        setOfficeState,
        setOfficeCity
      );
    } else {
      setOfficeStateList([]);
      setOfficeCityList([]);
    }
  }, [officeCountry]);

  const findAndSetOfficeCountry = (countryName) => {
    const matchedCountry = countryList.find(country => country.name === countryName);
    if (matchedCountry) {
      setOfficeCountry(matchedCountry);
    }
  };
  useEffect(() => {
    if (officeCountry?.name && countryList.length > 0) {
      findAndSetOfficeCountry(officeCountry.name);
    }
  }, [userDetail]);

  const findAndSetOfficeState = (stateName) => {
    const matchedState = stateList.find(state => state.name === stateName);
    if (matchedState) {
      setOfficeState(matchedState);
    }
  };


  const findAndSetOfficeCity = (cityName) => {
    const matchedCity = cityList.find(city => city.name === cityName);
    if (matchedCity) {
      setOfficeCity(matchedCity);
    }
  };

  useEffect(() => {
    // Call these functions when the component mounts or when the lists are updated
    if (officeState?.name && officeStateList.length > 0) {
      findAndSetOfficeState(officeState.name);
    }
    if (officeCity?.name && officeCityList.length > 0) {
      findAndSetOfficeCity(officeCity.name);
    }
  }, [officeStateList, officeCityList, officeState?.name, officeCity?.name]);

  useEffect(() => {
    if (officeState) {
      const cities =
        officeStateList.find((s) => s.name === officeState?.name)?.cities || [];
      setOfficeCityList(cities);
    } else {
      setOfficeCityList([]);
    }
  }, [officeState]);

  console.log("userDetail ==>>", userDetail)

  // Render all form sections
  const renderAllFormSections = () => {
    return (
      <>
        {/* Basic Information */}
        {renderFormSection(
          "Personal Information",
          <Person color="primary" />,
          <Grid
            container
            spacing={isMobile ? 2 : 3}
            className={classes.mobileGrid}
          >
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="First Name"
                variant="outlined"
                value={userDetail?.first_name || ''}
                onChange={(e) => {
                  setUserDetail({
                    ...userDetail,
                    first_name: e.target.value,
                  });
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      className={classes.mobileInputAdornment}
                    >
                      <Person fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Last Name"
                variant="outlined"
                value={userDetail?.last_name || ''}
                onChange={(e) => {
                  setUserDetail({
                    ...userDetail,
                    last_name: e.target.value,
                  });
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      className={classes.mobileInputAdornment}
                    >
                      <Person fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box className={classes.phoneFieldContainer}>
                <Box className={classes.phoneCodeContainer}>
                  <FormControl fullWidth>
                    {/* <FormLabel className={classes.formLabel}>Phone Code</FormLabel> */}
                    <CSelect
                      data={phoneCodeList}
                      fullObj
                      placeholder="Code"
                      value={selectedPhoneCode}
                      onSelect={(val) => setSelectedPhoneCode(val)}
                      phoneCode
                    />
                  </FormControl>
                </Box>
                <Box className={classes.phoneNumberContainer}>
                  <TextField
                    fullWidth
                    label="Mobile Number"
                    variant="outlined"
                    type="tel"
                    value={userDetail?.phone || ''}
                    onChange={(e) => {
                      // Only allow numbers and common phone characters
                      const phoneValue = e.target.value.replace(/[^0-9+\-\s()]/g, '');
                      setUserDetail({
                        ...userDetail,
                        phone: phoneValue,
                      });

                      // Real-time validation
                      const error = validatePhoneNumber(phoneValue, false);
                      setPhoneError(error);
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment
                          position="start"
                          className={classes.mobileInputAdornment}
                        >
                          <Phone fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    placeholder="Enter mobile number"
                    helperText={
                      phoneError
                        ? phoneError
                        : userDetail?.phone && userDetail.phone.trim() !== "" && !phoneError
                          ? `✓ Valid phone number: ${selectedPhoneCode?.phone_code || ''} ${userDetail.phone}`
                          : "Enter phone number (e.g., 9876543210)"
                    }
                    error={!!phoneError}
                    color={userDetail?.phone && userDetail.phone.trim() !== "" && !phoneError ? "success" : "primary"}
                    inputProps={{
                      maxLength: 15,
                    }}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company Name"
                variant="outlined"
                value={userDetail?.businessInfo?.name || ''}
                onChange={(e) => {
                  // setCompanyName(e.target.value);
                  setUserDetail({
                    ...userDetail,
                    businessInfo: {
                      ...userDetail.businessInfo,
                      name: e.target.value,
                    },
                  });
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      className={classes.mobileInputAdornment}
                    >
                      <Business fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            {/* Company CEO Name Field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company CEO Name"
                variant="outlined"
                value={userDetail?.businessInfo?.company_ceo_name || ''}
                onChange={(e) => {
                  setUserDetail({
                    ...userDetail,
                    businessInfo: {
                      ...userDetail.businessInfo,
                      company_ceo_name: e.target.value,
                    },
                  });
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      className={classes.mobileInputAdornment}
                    >
                      <Person fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                placeholder="Enter CEO's name"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                variant="outlined"
                value={userDetail?.email || ''}
                onChange={(e) => {
                  // setEmail(e.target.value);
                  setUserDetail({
                    ...userDetail,
                    email: e.target.value,
                  });
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      className={classes.mobileInputAdornment}
                    >
                      <Email fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        )}

        {/* Meta Description Section */}
        {renderFormSection(
          "Meta Description",
          <Description color="primary" />, // You can use a different icon if preferred
          <Box sx={{ width: '100%', maxHeight: isMobile ? 420 : 520, overflowY: 'auto' }}>
            <Grid container spacing={isMobile ? 2 : 3} className={classes.mobileGrid}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={applyGlobalMeta}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setApplyGlobalMeta(checked);
                          if (checked) {
                            const globalTitle = userDetail?.businessInfo?.meta_title || "";
                            const globalDesc = userDetail?.businessInfo?.meta_description || "";
                            setUserDetail(prev => ({
                              ...prev,
                              businessInfo: {
                                ...(prev?.businessInfo || {}),
                                meta_title_about_us: globalTitle,
                                meta_description_about_us: globalDesc,
                                meta_title_contact_us: globalTitle,
                                meta_description_contact_us: globalDesc,
                                meta_title_our_products: globalTitle,
                                meta_description_our_products: globalDesc,
                              },
                            }));
                            setMetaKeywordsAboutUs(metaKeywords);
                            setMetaKeywordsContactUs(metaKeywords);
                            setMetaKeywordsOurProducts(metaKeywords);
                          } else {
                            setUserDetail(prev => ({
                              ...prev,
                              businessInfo: {
                                ...(prev?.businessInfo || {}),
                                meta_title_about_us: "",
                                meta_description_about_us: "",
                                meta_title_contact_us: "",
                                meta_description_contact_us: "",
                                meta_title_our_products: "",
                                meta_description_our_products: "",
                              },
                            }));
                            setMetaKeywordsAboutUs([]);
                            setMetaKeywordsContactUs([]);
                            setMetaKeywordsOurProducts([]);
                          }
                        }}
                      />
                    }
                    label="Use above meta for About Us, Contact Us, and Our Products"
                  />
                </Box>
              </Grid>
              {/* Global Meta */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Meta Title"
                  variant="outlined"
                  value={userDetail?.businessInfo?.meta_title || ''}
                  onChange={e => setUserDetail({
                    ...userDetail,
                    businessInfo: {
                      ...userDetail.businessInfo,
                      meta_title: e.target.value,
                    },
                  })}
                  placeholder="Enter meta title"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Meta Description"
                  variant="outlined"
                  value={userDetail?.businessInfo?.meta_description || ''}
                  onChange={e => setUserDetail({
                    ...userDetail,
                    businessInfo: {
                      ...userDetail.businessInfo,
                      meta_description: e.target.value,
                    },
                  })}
                  placeholder="Enter meta description"
                  multiline
                  minRows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <TagsInput
                  onKeyDown={e => {
                    if (e.key === "Enter" && metaKeywordInput.trim() !== "") {
                      setMetaKeywords([...metaKeywords, metaKeywordInput.trim()]);
                      setMetaKeywordInput("");
                    }
                  }}
                  value={metaKeywordInput}
                  onChange={val => setMetaKeywordInput(val)}
                  tagList={metaKeywords}
                  removeTag={index => setMetaKeywords(metaKeywords.filter((_, i) => i !== index))}
                  errorMsg={""}
                />
              </Grid>

              {/* About Us Meta */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="About Us Meta Title"
                  variant="outlined"
                  value={userDetail?.businessInfo?.meta_title_about_us || ''}
                  onChange={e => setUserDetail({
                    ...userDetail,
                    businessInfo: {
                      ...userDetail.businessInfo,
                      meta_title_about_us: e.target.value,
                    },
                  })}
                  placeholder="Enter About Us meta title"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="About Us Meta Description"
                  variant="outlined"
                  value={userDetail?.businessInfo?.meta_description_about_us || ''}
                  onChange={e => setUserDetail({
                    ...userDetail,
                    businessInfo: {
                      ...userDetail.businessInfo,
                      meta_description_about_us: e.target.value,
                    },
                  })}
                  placeholder="Enter About Us meta description"
                  multiline
                  minRows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <TagsInput
                  onKeyDown={e => {
                    if (e.key === "Enter" && metaKeywordInputAboutUs.trim() !== "") {
                      setMetaKeywordsAboutUs([...metaKeywordsAboutUs, metaKeywordInputAboutUs.trim()]);
                      setMetaKeywordInputAboutUs("");
                    }
                  }}
                  value={metaKeywordInputAboutUs}
                  onChange={val => setMetaKeywordInputAboutUs(val)}
                  tagList={metaKeywordsAboutUs}
                  removeTag={index => setMetaKeywordsAboutUs(metaKeywordsAboutUs.filter((_, i) => i !== index))}
                  errorMsg={""}
                />
              </Grid>

              {/* Contact Us Meta */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Contact Us Meta Title"
                  variant="outlined"
                  value={userDetail?.businessInfo?.meta_title_contact_us || ''}
                  onChange={e => setUserDetail({
                    ...userDetail,
                    businessInfo: {
                      ...userDetail.businessInfo,
                      meta_title_contact_us: e.target.value,
                    },
                  })}
                  placeholder="Enter Contact Us meta title"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Contact Us Meta Description"
                  variant="outlined"
                  value={userDetail?.businessInfo?.meta_description_contact_us || ''}
                  onChange={e => setUserDetail({
                    ...userDetail,
                    businessInfo: {
                      ...userDetail.businessInfo,
                      meta_description_contact_us: e.target.value,
                    },
                  })}
                  placeholder="Enter Contact Us meta description"
                  multiline
                  minRows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <TagsInput
                  onKeyDown={e => {
                    if (e.key === "Enter" && metaKeywordInputContactUs.trim() !== "") {
                      setMetaKeywordsContactUs([...metaKeywordsContactUs, metaKeywordInputContactUs.trim()]);
                      setMetaKeywordInputContactUs("");
                    }
                  }}
                  value={metaKeywordInputContactUs}
                  onChange={val => setMetaKeywordInputContactUs(val)}
                  tagList={metaKeywordsContactUs}
                  removeTag={index => setMetaKeywordsContactUs(metaKeywordsContactUs.filter((_, i) => i !== index))}
                  errorMsg={""}
                />
              </Grid>

              {/* Our Products Meta */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Our Products Meta Title"
                  variant="outlined"
                  value={userDetail?.businessInfo?.meta_title_our_products || ''}
                  onChange={e => setUserDetail({
                    ...userDetail,
                    businessInfo: {
                      ...userDetail.businessInfo,
                      meta_title_our_products: e.target.value,
                    },
                  })}
                  placeholder="Enter Our Products meta title"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Our Products Meta Description"
                  variant="outlined"
                  value={userDetail?.businessInfo?.meta_description_our_products || ''}
                  onChange={e => setUserDetail({
                    ...userDetail,
                    businessInfo: {
                      ...userDetail.businessInfo,
                      meta_description_our_products: e.target.value,
                    },
                  })}
                  placeholder="Enter Our Products meta description"
                  multiline
                  minRows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <TagsInput
                  onKeyDown={e => {
                    if (e.key === "Enter" && metaKeywordInputOurProducts.trim() !== "") {
                      setMetaKeywordsOurProducts([...metaKeywordsOurProducts, metaKeywordInputOurProducts.trim()]);
                      setMetaKeywordInputOurProducts("");
                    }
                  }}
                  value={metaKeywordInputOurProducts}
                  onChange={val => setMetaKeywordInputOurProducts(val)}
                  tagList={metaKeywordsOurProducts}
                  removeTag={index => setMetaKeywordsOurProducts(metaKeywordsOurProducts.filter((_, i) => i !== index))}
                  errorMsg={""}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Business Type */}
        {renderFormSection(
          "Business Type",
          <WorkOutline color="primary" />,
          <FormControl className={classes.formControl} fullWidth>
            <FormGroup className={classes.formGroup}>
              <Grid
                container
                spacing={isMobile ? 1 : 2}
                className={classes.mobileGrid}
              >
                {[
                  "Manufacturer",
                  "Wholesaler",
                  "Trader",
                  "Buyer",
                  "Seller",
                  "Retailer",
                ].map((type) => (
                  <Grid item xs={12} sm={6} md={4} key={type}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          checked={
                            Array.isArray(userDetail?.businessInfo?.business_type) &&
                            userDetail.businessInfo.business_type.includes(type)
                          }
                          onChange={(e) => {
                            const currentBusinessTypes = userDetail?.businessInfo?.business_type || [];
                            const updatedBusinessType = e.target.checked
                              ? [...currentBusinessTypes, type]
                              : currentBusinessTypes.filter((item) => item !== type);

                            setUserDetail({
                              ...userDetail,
                              businessInfo: {
                                ...userDetail.businessInfo,
                                business_type: updatedBusinessType,
                              },
                            });
                          }}
                        />
                      }
                      label={type}
                      className={classes.formControlLabel}
                    />
                  </Grid>
                ))}
              </Grid>
            </FormGroup>
          </FormControl>
        )}

        {/* Company Details */}
        {renderFormSection(
          "Company Size & Financials",
          <AttachMoney color="primary" />,
          <Grid
            container
            spacing={isMobile ? 2 : 3}
            className={classes.mobileGrid}
          >
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <FormLabel className={classes.formLabel}>
                  Total Employees
                </FormLabel>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={userDetail?.businessInfo?.total_no_of_employee || ''}
                  onChange={(e) => {
                    // setTotalEmployees(e.target.value);
                    setUserDetail({
                      ...userDetail,
                      businessInfo: {
                        ...userDetail.businessInfo,
                        total_no_of_employee: e.target.value,
                      },
                    });
                  }}
                  placeholder="Enter total number of employees"
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <FormLabel className={classes.formLabel}>
                  Annual Turnover
                </FormLabel>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={userDetail?.businessInfo?.annual_turn_over || ''}
                  onChange={(e) => {
                    setUserDetail({
                      ...userDetail,
                      businessInfo: {
                        ...userDetail.businessInfo,
                        annual_turn_over: e.target.value,
                      },
                    });
                  }}
                  placeholder="Enter annual turnover"
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <FormLabel className={classes.formLabel}>
                  Founding Year
                </FormLabel>
                <Select
                  labelId="founding-year-label"
                  value={userDetail?.businessInfo?.company_founding_year || ''}
                  onChange={(e) => {
                    const year = e.target.value;
                    setUserDetail((prev) => ({
                      ...prev,
                      businessInfo: {
                        ...prev.businessInfo,
                        company_founding_year: year,
                      },
                    }));
                  }}
                >
                  {years.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid >
        )}

        {/* Company Description */}
        {
          renderFormSection(
            "Company Description",
            <Description color="primary" />,
            <Grid
              container
              spacing={isMobile ? 2 : 3}
              className={classes.mobileGrid}
            >
              <Grid item xs={12}>
                <Box sx={{ width: "100%" }}>
                  <Typography gutterBottom>Company Details</Typography>
                  <Editor
                    apiKey={BaseSetting?.tinymceEditorApiKey}
                    onInit={(evt, editor) => (editorRef.current = editor)}
                    value={userDetail?.businessInfo?.company_description || ''}
                    init={{
                      height: isMobile ? 300 : 400,
                      menubar: false,
                      plugins: [
                        "advlist autolink lists link image charmap print preview anchor",
                        "searchreplace visualblocks code fullscreen",
                        "insertdatetime media table paste code help wordcount image",
                      ],
                      toolbar:
                        "undo redo | formatselect | " +
                        "bold italic backcolor | alignleft aligncenter " +
                        "alignright alignjustify | bullist numlist outdent indent | " +
                        "removeformat | image | help",
                      content_style:
                        "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
                      images_upload_url: `${BaseSetting?.serverUrl}${BaseSetting?.endpoint?.imageUpload}`,
                      images_upload_handler: function (
                        blobInfo,
                        success,
                        failure
                      ) {
                        const formData = new FormData();
                        formData.append(
                          "file",
                          blobInfo.blob(),
                          blobInfo.filename()
                        );

                        fetch(
                          `${BaseSetting?.serverUrl}${BaseSetting?.endpoint?.imageUpload}`,
                          {
                            method: "POST",
                            body: formData,
                            headers: {
                              Authorization: `Bearer ${localStorage.getItem(
                                "token"
                              )}`,
                            },
                          }
                        )
                          .then((response) => response.json())
                          .then((result) => {
                            if (result.status) {
                              success(result.data.url);
                            } else {
                              failure("Image upload failed");
                            }
                          })
                          .catch((error) => {
                            failure("Image upload failed");
                          });
                      },
                    }}
                    onEditorChange={(content) => {
                      setUserDetail({
                        ...userDetail,
                        businessInfo: {
                          ...userDetail.businessInfo,
                          company_description: content,
                        },
                      });
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <FormLabel component="legend" style={{ marginBottom: 8 }}>
                  Why Choose Us?
                </FormLabel>
                <TextareaAutosize
                  minRows={isMobile ? 2 : 3}
                  placeholder="What makes your company stand out?"
                  className={classes.textarea}
                  value={userDetail?.businessInfo?.why_choose_us || ''}
                  onChange={(e) => {
                    setUserDetail({
                      ...userDetail,
                      businessInfo: {
                        ...userDetail.businessInfo,
                        why_choose_us: e.target.value,
                      },
                    });
                  }}
                />
              </Grid>
            </Grid>
          )
        }

        {/* Media Gallery */}
        {
          renderFormSection(
            "Media Gallery",
            <PhotoLibrary color="primary" />,
            <Grid
              container
              spacing={isMobile ? 2 : 3}
              className={classes.mobileGrid}
            >
              <Grid item xs={12}>
                <Stack spacing={2} className={classes.mobileStack}>
                  <Box>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<AddPhotoAlternate />}
                      disabled={photos?.length >= 5}
                      className={isMobile ? classes.buttonOutlined : ""}
                    >
                      Upload Photos
                      <input
                        hidden
                        accept="image/*"
                        multiple
                        type="file"
                        onChange={handlePhotoUpload}
                      />
                    </Button>
                    <Typography
                      variant="caption"
                      sx={{ ml: isMobile ? 0 : 2, color: "text.secondary" }}
                      className={classes.mobileTypography}
                    >
                      {`Uploaded ${photos?.length} of 5 photos (each <1MB)`}
                    </Typography>

                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
                      {photoPreviews?.map((src, index) => (
                        <Box key={index} sx={{ position: "relative", width: 80, height: 80 }}>
                          <img
                            src={src}
                            alt={`preview-${index}`}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              borderRadius: 4,
                            }}
                          />
                          <IconButton
                            size="small"
                            sx={{
                              position: "absolute",
                              top: -8,
                              right: -8,
                              bgcolor: "white",
                              border: "1px solid #ccc",
                            }}
                            onClick={() => removePhoto(index)}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  <Box>
                    <FormLabel className={classes.formLabel}>Video</FormLabel>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexDirection: isMobile ? "column" : "row",
                        gap: isMobile ? 1 : 0,
                      }}
                    >
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<Videocam />}
                        className={isMobile ? classes.buttonOutlined : ""}
                        disabled={!!companyVideo}
                      >
                        {companyVideo ? "Video Selected" : "Upload Video"}
                        <input hidden accept="video/*" type="file" onChange={handleCompanyVideoUpload} />
                      </Button>
                      <Typography
                        variant="caption"
                        sx={{ ml: isMobile ? 0 : 2, color: "text.secondary" }}
                        className={classes.mobileTypography}
                      >
                        Optional: Upload a video file (max 20MB)
                      </Typography>
                      {companyVideoPreview && (
                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 2, mt: isMobile ? 1 : 0 }}>
                          <video src={companyVideoPreview} controls width={120} height={80} style={{ borderRadius: 4, background: '#000' }} />
                          <IconButton size="small" onClick={removeCompanyVideo} color="error" sx={{ ml: 1 }}>
                            <Close fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* YouTube Link Field and Preview */}
                  <Box sx={{ mt: 2 }}>
                    <FormLabel className={classes.formLabel}>YouTube Link</FormLabel>
                    <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', gap: 2 }}>
                      <TextField
                        fullWidth
                        placeholder="Paste YouTube video link here"
                        variant="outlined"
                        value={userDetail?.businessInfo?.youtube_link || ''}
                        onChange={e => {
                          setUserDetail({
                            ...userDetail,
                            businessInfo: {
                              ...userDetail.businessInfo,
                              youtube_link: e.target.value,
                            },
                          });
                        }}
                      />
                      {/* YouTube Preview */}
                      {userDetail?.businessInfo?.youtube_link && (
                        (() => {
                          // Extract YouTube video ID from URL
                          const match = userDetail.businessInfo.youtube_link.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/);
                          const videoId = match ? match[1] : null;
                          return videoId ? (
                            <Box sx={{ minWidth: 180, maxWidth: 240 }}>
                              <iframe
                                width="100%"
                                height="120"
                                src={`https://www.youtube.com/embed/${videoId}`}
                                title="YouTube video preview"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              />
                            </Box>
                          ) : null;
                        })()
                      )}
                    </Box>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          )
        }

        {/* Company Brochure */}
        {renderFormSection(
          "Company Brochure",
          <DescriptionIcon color="primary" />,
          <Grid
            container
            spacing={isMobile ? 2 : 3}
            className={classes.mobileGrid}
          >
            <Grid item xs={12}>
              <Stack spacing={2} className={classes.mobileStack}>
                <Box>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<DescriptionIcon />}
                    className={isMobile ? classes.buttonOutlined : ""}
                  >
                    {hasCompanyBrochure() ? "Replace Company Brochure" : "Upload Company Brochure"}
                    <input
                      hidden
                      accept=".pdf,.doc,.docx"
                      type="file"
                      onChange={handleCompanyBrochureUpload}
                    />
                  </Button>
                  <Typography
                    variant="caption"
                    sx={{ ml: isMobile ? 0 : 2, color: "text.secondary" }}
                    className={classes.mobileTypography}
                  >
                    Upload PDF or DOC/DOCX file (max 5MB)
                  </Typography>

                  {hasCompanyBrochure() && (
                    <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        icon={<DescriptionIcon />}
                        label={getCompanyBrochureDisplayName()}
                        variant="outlined"
                        color={companyBrochure instanceof File ? "primary" : "success"}
                        onClick={companyBrochure instanceof File ? undefined : () => {
                          // Open existing brochure in new tab
                          if (userDetail?.businessInfo?.company_brochure) {
                            window.open(userDetail.businessInfo.company_brochure, '_blank');
                          }
                        }}
                        sx={{
                          cursor: companyBrochure instanceof File ? 'default' : 'pointer',
                          '&:hover': companyBrochure instanceof File ? {} : { backgroundColor: 'rgba(76, 175, 80, 0.1)' }
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={removeCompanyBrochure}
                        color="error"
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Stack>
            </Grid>
          </Grid>
        )}

        {/* Address Information */}
        {
          renderFormSection(
            "User Address",
            <LocationOn color="primary" />,
            <Grid
              container
              spacing={isMobile ? 2 : 3}
              className={classes.mobileGrid}
            >
              <Grid item xs={12} md={6}>
                <FormControl fullWidth className={classes.addressSelect}>
                  <FormLabel className={classes.formLabel}>Country</FormLabel>
                  <Grid item xs={12} sm={5.8} md={5.87} lg={5.79}>
                    <CSelect
                      country
                      data={countryList}
                      fullObj
                      placeholder="Country"
                      value={userCountry}
                      onSelect={(val) => {
                        console.log("val ==>>", val)
                        setUserCountry(val);
                      }}
                    // errorMsg={countryErrorText}
                    />
                  </Grid>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  className={classes.addressSelect}
                  disabled={!userCountry}
                >
                  <FormLabel className={classes.formLabel}>State</FormLabel>
                  <CSelect
                    data={stateList}
                    fullObj
                    placeholder="Select State"
                    value={userState}
                    onSelect={(val) => setUserState(val)}
                    disabled={!userCountry || stateList.length === 0}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  className={classes.addressSelect}
                  disabled={!userState}
                >
                  <FormLabel className={classes.formLabel}>City</FormLabel>
                  <CSelect
                    data={cityList}
                    fullObj
                    placeholder="Select City"
                    value={userCity}
                    onSelect={(val) => setUserCity(val)}
                    disabled={!userState || cityList.length === 0}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth className={classes.addressSelect}>
                  <FormLabel className={classes.formLabel}>Postal Code</FormLabel>
                  <TextField
                    fullWidth
                    placeholder="Postal Code"
                    variant="outlined"
                    value={userDetail?.businessInfo?.zipcode || ''}
                    onChange={(e) => {
                      const newZip = e.target.value;
                      setUserDetail((prev) => ({
                        ...prev,
                        businessInfo: {
                          ...prev.businessInfo,
                          zipcode: newZip,
                        },
                      }));
                    }}

                  />

                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth className={classes.addressSelect}>
                  <FormLabel className={classes.formLabel}>Full User Address</FormLabel>
                  <TextareaAutosize
                    minRows={3}
                    placeholder="Enter full address"
                    className={classes.textarea}
                    value={userDetail?.businessInfo?.full_user_address || ''}
                    onChange={(e) => setUserDetail({
                      ...userDetail,
                      businessInfo: {
                        ...userDetail.businessInfo,
                        full_user_address: e.target.value,
                      },
                    })}
                  />
                </FormControl>
              </Grid>
            </Grid>
          )
        }

        {/* Office Address */}
        {
          renderFormSection(
            "Office Address",
            <Business color="primary" />,
            <Grid
              container
              spacing={isMobile ? 2 : 3}
              className={classes.mobileGrid}
            >
              <Grid item xs={12} md={6}>
                <FormControl fullWidth className={classes.addressSelect}>
                  <FormLabel className={classes.formLabel}>Country</FormLabel>
                  <CSelect
                    country
                    data={countryList}
                    fullObj
                    placeholder="Select Country"
                    value={officeCountry}
                    onSelect={(val) => setOfficeCountry(val)}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  className={classes.addressSelect}
                  disabled={!officeCountry}
                >
                  <FormLabel className={classes.formLabel}>State</FormLabel>
                  <CSelect
                    data={officeStateList}
                    fullObj
                    placeholder="Select State"
                    value={officeState}
                    onSelect={(val) => {
                      setOfficeState(val);
                    }}
                    disabled={
                      !officeCountry || officeStateList.length === 0
                    }
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  className={classes.addressSelect}
                  disabled={!officeState}
                >
                  <FormLabel className={classes.formLabel}>City</FormLabel>
                  <CSelect
                    data={officeCityList}
                    fullObj
                    placeholder="Select City"
                    value={officeCity}
                    onSelect={(val) => {
                      setOfficeCity(val);
                    }}
                    disabled={!officeState || officeCityList.length === 0}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth className={classes.addressSelect}>
                  <FormLabel className={classes.formLabel}>Postal Code</FormLabel>
                  <TextField
                    fullWidth
                    placeholder="Postal Code"
                    variant="outlined"
                    value={userDetail?.businessInfo?.office_zipcode || ''}
                    onChange={(e) => setUserDetail({
                      ...userDetail,
                      businessInfo: {
                        ...userDetail.businessInfo,
                        office_zipcode: e.target.value,
                      },
                    })}
                  // onBlur={() => pincodeValidate(officePostalCode, setOfficeCountry, setOfficeState, setOfficeCity)}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth className={classes.addressSelect}>
                  <FormLabel className={classes.formLabel}>Full OfficeAddress</FormLabel>
                  <TextareaAutosize
                    minRows={3}
                    placeholder="Enter full address"
                    className={classes.textarea}
                    value={userDetail?.businessInfo?.full_office_address || ''}
                    onChange={(e) => setUserDetail({
                      ...userDetail,
                      businessInfo: {
                        ...userDetail.businessInfo,
                        full_office_address: e.target.value,
                      },
                    })}
                  />
                </FormControl>
              </Grid>
            </Grid>
          )
        }

        {/* Warehouse Information */}
        {
          renderFormSection(
            "Warehouse Information",
            <Warehouse color="primary" />,
            <Grid
              container
              spacing={isMobile ? 2 : 3}
              className={classes.mobileGrid}
            >
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Warehouse Address"
                  variant="outlined"
                  placeholder="Enter warehouse location"
                  value={userDetail?.businessInfo?.warehouse_address || ''}
                  onChange={(e) => setUserDetail({
                    ...userDetail,
                    businessInfo: {
                      ...userDetail.businessInfo,
                      warehouse_address: e.target.value,
                    },
                  })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        position="start"
                        className={classes.mobileInputAdornment}
                      >
                        <Warehouse fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormLabel component="legend" style={{ marginBottom: 8 }}>
                  Warehouse Details
                </FormLabel>
                <TextareaAutosize
                  minRows={isMobile ? 2 : 3}
                  placeholder="Enter detailed information about the warehouse"
                  className={classes.textarea}
                  value={userDetail?.businessInfo?.warehouse_details || ''}
                  onChange={(e) => {
                    setUserDetail({
                      ...userDetail,
                      businessInfo: {
                        ...userDetail.businessInfo,
                        warehouse_details: e.target.value,
                      },
                    });
                  }}
                />
              </Grid>
            </Grid>
          )
        }

        {/* GST Information */}
        {
          renderFormSection(
            "GST Information",
            <Gavel color="primary" />,
            <Grid
              container
              spacing={isMobile ? 2 : 3}
              className={classes.mobileGrid}
            >
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="GST Number"
                  variant="outlined"
                  type="text"
                  value={userDetail?.businessInfo?.GST_number || ''}
                  onChange={(e) =>
                    setUserDetail({
                      ...userDetail,
                      businessInfo: {
                        ...userDetail.businessInfo,
                        GST_number: e.target.value.toUpperCase(), // common for GST
                      },
                    })
                  }
                  inputProps={{
                    maxLength: 15,
                    pattern: "[0-9A-Z]{15}",
                  }}
                  helperText="Enter 15-character GSTIN"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="GST Partner Name"
                  variant="outlined"
                  type="text"
                  value={userDetail?.businessInfo?.GST_partner_name || ''}
                  onChange={(e) =>
                    setUserDetail({
                      ...userDetail,
                      businessInfo: {
                        ...userDetail.businessInfo,
                        GST_partner_name: e.target.value,
                      },
                    })
                  }
                  helperText="Enter name as per GST records"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="GST Registration Date"
                  type="date"
                  variant="outlined"
                  value={
                    userDetail?.businessInfo?.GST_registration_date
                      ? userDetail.businessInfo.GST_registration_date.slice(0, 10)
                      : ""
                  }
                  onChange={(e) =>
                    setUserDetail((prev) => ({
                      ...prev,
                      businessInfo: {
                        ...prev.businessInfo,
                        GST_registration_date: e.target.value,
                      },
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

            </Grid>
          )
        }

        {/* Payment Methods */}
        {
          renderFormSection(
            "Payment Methods",
            <Payment color="primary" />,
            <FormControl className={classes.formControl} fullWidth>
              <FormGroup className={classes.formGroup}>
                <Grid
                  container
                  spacing={isMobile ? 1 : 2}
                  className={classes.mobileGrid}
                >
                  {["By Cash", "By Credit card", "By Cheque", "By DD"].map(
                    (method) => (
                      <Grid item xs={12} sm={6} key={method}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="primary"
                              checked={
                                Array.isArray(userDetail?.businessInfo?.payment_method) &&
                                userDetail.businessInfo.payment_method.includes(method)
                              }
                              onChange={(e) => {
                                const currentPaymentMethods = userDetail?.businessInfo?.payment_method || [];
                                const updatedPaymentMethods = e.target.checked
                                  ? [...currentPaymentMethods, method]
                                  : currentPaymentMethods.filter((item) => item !== method);

                                setUserDetail({
                                  ...userDetail,
                                  businessInfo: {
                                    ...userDetail.businessInfo,
                                    payment_method: updatedPaymentMethods,
                                  },
                                });
                              }}
                            />
                          }
                          label={method}
                          className={classes.formControlLabel}
                        />
                      </Grid>
                    )
                  )}
                </Grid>
              </FormGroup>
            </FormControl>
          )
        }

        {/* Our Aim and Our Vision */}
        {renderFormSection(
          "Our Aim and Our Vision",
          <ThumbUp color="primary" />,
          <Grid container spacing={isMobile ? 2 : 3} className={classes.mobileGrid}>
            <Grid item xs={12}>
              <FormControl fullWidth className={classes.addressSelect}>
                <FormLabel className={classes.formLabel}>Our Aim</FormLabel>
                <TextareaAutosize
                  minRows={3}
                  placeholder="Enter your company's aim"
                  className={classes.textarea}
                  value={userDetail?.businessInfo?.our_aim || ''}
                  onChange={(e) => setUserDetail({
                    ...userDetail,
                    businessInfo: {
                      ...userDetail.businessInfo,
                      our_aim: e.target.value,
                    },
                  })}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth className={classes.addressSelect}>
                <FormLabel className={classes.formLabel}>Our Vision</FormLabel>
                <TextareaAutosize
                  minRows={3}
                  placeholder="Enter your company's vision"
                  className={classes.textarea}
                  value={userDetail?.businessInfo?.our_vision || ''}
                  onChange={(e) => setUserDetail({
                    ...userDetail,
                    businessInfo: {
                      ...userDetail.businessInfo,
                      our_vision: e.target.value,
                    },
                  })}
                />
              </FormControl>
            </Grid>
          </Grid>
        )}

        {/* Shipment Modes */}
        {
          renderFormSection(
            "Shipment Modes",
            <LocalShipping color="primary" />,
            <FormControl className={classes.formControl} fullWidth>
              <FormGroup className={classes.formGroup}>
                <Grid
                  container
                  spacing={isMobile ? 1 : 2}
                  className={classes.mobileGrid}
                >
                  {["By Air", "By Train", "By Road", "By Cargo", "By Sea"].map(
                    (mode) => (
                      <Grid item xs={12} sm={6} key={mode}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="primary"
                              checked={
                                Array.isArray(userDetail?.businessInfo?.shipment_method) &&
                                userDetail.businessInfo.shipment_method.includes(mode)
                              }
                              onChange={(e) => {
                                const currentShipmentModes = userDetail?.businessInfo?.shipment_method || [];
                                const updatedShipmentModes = e.target.checked
                                  ? [...currentShipmentModes, mode]
                                  : currentShipmentModes.filter((item) => item !== mode);

                                setUserDetail({
                                  ...userDetail,
                                  businessInfo: {
                                    ...userDetail.businessInfo,
                                    shipment_method: updatedShipmentModes,
                                  },
                                });
                              }}
                            />
                          }
                          label={mode}
                          className={classes.formControlLabel}
                        />
                      </Grid>
                    )
                  )}
                </Grid>
              </FormGroup>
            </FormControl>
          )
        }

        {/* Legal Status */}
        {
          renderFormSection(
            "Legal Status",
            <Gavel color="primary" />,
            <Grid
              container
              spacing={isMobile ? 2 : 3}
              className={classes.mobileGrid}
            >
              <Grid item xs={12}>
                <FormControl className={classes.formControl} fullWidth>
                  <FormLabel className={classes.formLabel}>
                    Legal Status of Firm
                  </FormLabel>
                  <RadioGroup
                    className={classes.radioGroup}
                    value={userDetail?.businessInfo?.legal_status_of_firm || ""}
                    onChange={(e) => {
                      setUserDetail({
                        ...userDetail,
                        businessInfo: {
                          ...userDetail.businessInfo,
                          legal_status_of_firm: e.target.value,
                        },
                      });
                    }}
                  >
                    {["Sole Proprietorship", "Partnership", "LLC", "Corporation (Inc.)", "Co-op", "Joint Venture", "Trust"].map((status) => (
                      <Grid item xs={12} sm={6} key={status}>
                        <FormControlLabel
                          value={status}
                          control={<Radio color="primary" />}
                          label={status}
                          className={classes.formControlLabel}
                        />
                      </Grid>
                    ))}
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Import/Export Code"
                  variant="outlined"
                  value={userDetail?.businessInfo?.import_export_code || ''}
                  onChange={(e) => {
                    const code = e.target.value;
                    setUserDetail((prev) => ({
                      ...prev,
                      businessInfo: {
                        ...prev.businessInfo,
                        import_export_code: code,
                      },
                    }));
                  }}
                />
              </Grid>

            </Grid>
          )
        }

        {/* Account Status */}
        {
          renderFormSection(
            "Account Status",
            <VerifiedUser color="primary" />,
            <Grid
              container
              spacing={isMobile ? 2 : 3}
              className={classes.mobileGrid}
            >
              <Grid item xs={12} md={6}>
                <FormControl className={classes.formControl} fullWidth>
                  <FormLabel className={classes.formLabel}>
                    Verification Status
                  </FormLabel>
                  <RadioGroup
                    row={!isMobile}
                    value={Number(userDetail?.is_verified)} // convert for comparison
                    onChange={(e) =>
                      setUserDetail({
                        ...userDetail,
                        is_verified: parseInt(e.target.value, 10),
                      })
                    }
                  >
                    <FormControlLabel
                      value={1}
                      control={<Radio color="success" />}
                      label="Verified"
                    />
                    <FormControlLabel
                      value={0}
                      control={<Radio color="error" />}
                      label="Not Verified"
                    />
                  </RadioGroup>

                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl className={classes.formControl} fullWidth>
                  <FormLabel className={classes.formLabel}>
                    Subscription Level
                  </FormLabel>
                  <RadioGroup
                    row={!isMobile}
                    value={subscriptionLevel}
                    onChange={(e) => setSubscriptionLevel(e.target.value)}
                  >
                    <FormControlLabel
                      value="free"
                      control={<Radio />}
                      label="Free"
                    />
                    <FormControlLabel
                      value="premium"
                      control={<Radio />}
                      label="Premium"
                    />
                    <FormControlLabel
                      value="gold"
                      control={<Radio />}
                      label="Gold"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
          )
        }

        {/* FAQ Section */}
        {renderFormSection(
          "FAQs (Frequently Asked Questions)",
          <Description color="primary" />,
          <Grid container spacing={isMobile ? 2 : 3} className={classes.mobileGrid}>
            {[0, 1, 2, 3, 4].map((idx) => (
              <React.Fragment key={idx}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={`Question ${idx + 1}`}
                    variant="outlined"
                    value={userDetail?.businessInfo?.faqs?.[idx]?.question || ''}
                    onChange={e => {
                      const newFaqs = userDetail?.businessInfo?.faqs ? [...userDetail?.businessInfo?.faqs] : Array(5).fill({ question: '', answer: '' });
                      newFaqs[idx] = { ...newFaqs[idx], question: e.target.value };
                      setUserDetail({
                        ...userDetail,
                        businessInfo: {
                          ...userDetail.businessInfo,
                          faqs: newFaqs,
                        },
                      });
                    }}
                    placeholder={`Enter question ${idx + 1}`}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={`Answer ${idx + 1}`}
                    variant="outlined"
                    value={userDetail?.businessInfo?.faqs?.[idx]?.answer || ''}
                    onChange={e => {
                      const newFaqs = userDetail?.businessInfo?.faqs ? [...userDetail.businessInfo.faqs] : Array(5).fill({ question: '', answer: '' });
                      newFaqs[idx] = { ...newFaqs[idx], answer: e.target.value };
                      setUserDetail({
                        ...userDetail,
                        businessInfo: {
                          ...userDetail.businessInfo,
                          faqs: newFaqs,
                        },
                      });
                    }}
                    placeholder={`Enter answer ${idx + 1}`}
                  />
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
        )}
      </>
    );
  };

  return (
    <MiniDrawer header="Company User Form">
      <Grid container sx={{ mb: 1 }}>
        <Button
          variant="contained"
          startIcon={<ArrowBack />}
          onClick={() => navigate("/company-users")}
        >
          Back
        </Button>
      </Grid>
      <Box className={classes.formContainer}>
        <Paper
          elevation={4}
          sx={{
            p: isMobile ? 2 : 3,
            mb: isMobile ? 2 : 3,
            borderRadius: 2,
            backgroundColor: BaseColor.primary,
            color: "#fff",
          }}
        >
          <Typography variant="h5" gutterBottom className={classes.formTitle}>
            Update Company User Details
          </Typography>
          <Typography variant="body1" className={classes.formSubtitle}>
            Update a company user profile with all relevant details
          </Typography>
        </Paper>

        {/* Company Logo Upload - PROMINENT AT TOP */}
        <Paper elevation={3} sx={{ p: isMobile ? 2 : 3, borderRadius: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: isMobile ? 'column' : 'row', gap: 2 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Company Logo</Typography>
              <Button
                variant="outlined"
                component="label"
                startIcon={<AddPhotoAlternate />}
                className={isMobile ? classes.buttonOutlined : ""}
                disabled={
                  (Array.isArray(companyLogoPreview) && companyLogoPreview[0] && typeof companyLogoPreview[0] === "string" && companyLogoPreview[0].trim() !== "") ||
                  (typeof companyLogoPreview === "string" && companyLogoPreview.trim() !== "")
                }
              >
                {(
                  (Array.isArray(companyLogoPreview) && companyLogoPreview[0] && typeof companyLogoPreview[0] === "string" && companyLogoPreview[0].trim() !== "") ||
                  (typeof companyLogoPreview === "string" && companyLogoPreview.trim() !== "")
                ) ? "Logo Selected" : "Upload Company Logo"}
                <input
                  hidden
                  accept="image/*"
                  type="file"
                  onChange={handleCompanyLogoUpload}
                />
              </Button>
              <Typography
                variant="caption"
                sx={{ ml: isMobile ? 0 : 2, color: "text.secondary" }}
                className={classes.mobileTypography}
              >
                Only one image allowed (max 1MB)
              </Typography>
            </Box>
            {/* Only show preview if companyLogoPreview is a non-empty string or a non-empty array with a valid string */}
            {Array.isArray(companyLogoPreview)
              ? companyLogoPreview[0] && typeof companyLogoPreview[0] === "string" && companyLogoPreview[0].trim() !== "" && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <img
                    src={companyLogoPreview[0]}
                    alt="Company Logo Preview"
                    style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4 }}
                  />
                  <IconButton size="small" onClick={removeCompanyLogo} color="error" sx={{ ml: 1 }}>
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              )
              : typeof companyLogoPreview === "string" && companyLogoPreview.trim() !== "" && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <img
                    src={companyLogoPreview}
                    alt="Company Logo Preview"
                    style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4 }}
                  />
                  <IconButton size="small" onClick={removeCompanyLogo} color="error" sx={{ ml: 1 }}>
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              )}
          </Box>
        </Paper>

        <Paper elevation={3} sx={{ p: isMobile ? 2 : 3, borderRadius: 2 }}>
          <Box sx={{ mt: isMobile ? 1 : 2 }}>
            {renderAllFormSections()}

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: isMobile ? 3 : 4,
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={updateUserDetails}
                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <Save />}
                size="large"
                className={isMobile ? classes.submitButton : ""}
                disabled={isLoading}
              >
                {isLoading ? "Updating..." : "Submit"}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box >
    </MiniDrawer >
  );
};

export default UserCompanyForm;
